import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, map, of } from 'rxjs';
import * as Cookies from 'es-cookie';
import { IChatAdapter, IChatMessage, IThread, IAgentResponse } from '../interfaces';

/**
 * n8n Chat Webhook adapter
 * Communicates with n8n workflow via webhook endpoint
 */
@Injectable({
	providedIn: 'root'
})
export class N8nChatAdapter implements IChatAdapter {
	private readonly _http = inject(HttpClient);

	// TODO: Configure these via environment or config service
	private _webhookUrl = 'https://your-n8n-instance.com/webhook/your-webhook-id';
	private _chatInputKey = 'chatInput';
	private _sessionKey = 'sessionId';
	private _includeAuthToken = true;
	private _additionalHeaders: Record<string, string> = {};

	/**
	 * Configure the n8n webhook URL
	 */
	configure(
		webhookUrl: string,
		chatInputKey = 'chatInput',
		sessionKey = 'sessionId',
		includeAuthToken = true,
		additionalHeaders: Record<string, string> = {}
	): void {
		this._webhookUrl = webhookUrl;
		this._chatInputKey = chatInputKey;
		this._sessionKey = sessionKey;
		this._includeAuthToken = includeAuthToken;
		this._additionalHeaders = additionalHeaders;
	}

	/**
	 * Get threads
	 * Returns empty array - threads should be managed by backend or parent service
	 */
	getThreads(): Observable<IThread[]> {
		return of([]);
	}

	/**
	 * Create a new thread
	 * Generate a unique session ID for n8n
	 */
	createThread(): Observable<IThread> {
		const newThread: IThread = {
			id: this._generateSessionId(),
			title: 'New Chat',
			lastMessage: '',
			lastMessageDate: new Date(),
			unreadCount: 0
		};

		return of(newThread);
	}

	/**
	 * Get messages for a thread
	 * Returns empty array - messages should be managed by backend or parent service
	 */
	getMessages(threadId: string): Observable<IChatMessage[]> {
		return of([]);
	}

	/**
	 * Send message to n8n webhook
	 */
	sendMessage(threadId: string, content: string): Observable<IAgentResponse> {
		const payload: any = {
			[this._chatInputKey]: content,
			[this._sessionKey]: threadId,
			action: 'sendMessage'
		};

		// Add token as metadata in payload if enabled
		if (this._includeAuthToken) {
			const token = this._getAuthToken();
			if (token) {
				payload.metadata = {
					authorization: token
				};
				console.log('[n8n] Auth token added to payload metadata');
			}
		}

		const options = this._includeAuthToken ? { headers: this._getHeaders() } : {};

		return this._http.post<any>(this._webhookUrl, payload, options).pipe(
			map((response) => {
				console.log('n8n response:', response);
				// n8n can return various formats, handle common cases
				const agentContent = this._extractContent(response);
				const messageId = `${Date.now()}`;
				const timestamp = new Date();

				return {
					content: agentContent,
					threadId,
					messageId: `agent-${messageId}`,
					timestamp
				};
			})
		);
	}

	/**
	 * Delete a thread
	 */
	deleteThread(threadId: string): Observable<void> {
		// No-op - thread deletion should be handled by backend or parent service
		return of(void 0);
	}

	/**
	 * Load previous session (optional - for context restoration)
	 */
	loadPreviousSession(threadId: string): Observable<any> {
		const payload = {
			[this._sessionKey]: threadId,
			action: 'loadPreviousSession'
		};

		const options = this._includeAuthToken ? { headers: this._getHeaders() } : {};

		return this._http.post<any>(this._webhookUrl, payload, options);
	}

	// Private helper methods

	private _getHeaders(): HttpHeaders {
		let headers = new HttpHeaders();

		// Add bearer token if available
		if (this._includeAuthToken) {
			const token = this._getAuthToken();
			if (token) {
				headers = headers.set('Authorization', `Bearer ${token}`);
				console.log('[n8n] Bearer token attached to request');
			} else {
				console.warn('[n8n] includeAuthToken is true but no token found in cookies');
			}
		}

		// Add any additional custom headers
		Object.keys(this._additionalHeaders).forEach((key) => {
			headers = headers.set(key, this._additionalHeaders[key]);
		});

		return headers;
	}

	private _getAuthToken(): string | null {
		// Get id_token from cookies (same as auth interceptor)
		const idToken = Cookies.get('id_token');
		return idToken || null;
	}

	private _generateSessionId(): string {
		return `session-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
	}

	private _extractContent(response: any): string {
		// Handle different n8n response formats
		if (typeof response === 'string') {
			return response;
		}
		
		// Format 1: {"output":"..."}
		if (response.output) {
			return typeof response.output === 'string' ? response.output : JSON.stringify(response.output);
		}
		
		// Format 2: {"response":{"text":"..."}}
		if (response.response?.text) {
			return response.response.text;
		}
		
		// Format 3: {"response":"..."}
		if (response.response && typeof response.response === 'string') {
			return response.response;
		}
		
		// Format 4: {"message":"..."}
		if (response.message) {
			return response.message;
		}
		
		// Format 5: {"text":"..."}
		if (response.text) {
			return response.text;
		}
		
		// Fallback: stringify the response
		return JSON.stringify(response);
	}
}
